import type {
  CreateOrganizationEventDto,
  UpdateOrganizationEventDto,
} from "../../../dtos";
import type { Endpoint } from "../../../endpoints";
import type {
  ArrayOptions,
  ArrayResult,
  Base,
  Currency,
  EventArtistRef,
  Location,
  OrganizationProfile,
} from "../..";
import type {
  OrganizationEventTicket,
  OrganizationEventTicketEndpoints,
} from "..";
import type { OrganizationEventOrderEndpoints } from "./orders";
import type { OrganizationEventPromoCodeEndpoints } from "./promo-codes";
import type {
  OrganizationEventStyle,
  OrganizationEventStyleEndpoints,
} from "./styles";
import type { OrganizationEventViewEndpoints } from "./views";

export * from "./orders";
export * from "./promo-codes";
export * from "./styles";
export * from "./tickets";
export * from "./views";

export type OrganizationEvent = Base & {
  title: string;
  description: string;
  slug: string;
  organization: OrganizationProfile;
  type: OrganizationEventType;
  visibility: OrganizationEventVisibilityType;
  currency: Currency;
  flyers: string[];
  trailers: string[];
  location: Location;
  tickets: OrganizationEventTicket[];
  styles: OrganizationEventStyle[];
  artists: EventArtistRef[];
  status: OrganizationEventStatus;
  viewsCount: number;
  visitsCount: number;
  visitorsCount: number;
  bouncesCount: number;
  totalDurationSeconds: number;
  averageViewsPerVisitorCount: number;
  bounceRate: number;
  averageVisitDurationSeconds: number;
  hypeCount: number;
  minPrice: number;
  startAt: Date;
  endAt: Date;
};

export enum OrganizationEventType {
  Clubbing = "clubbing",
  Concert = "concert",
  Festival = "festival",
  HouseParty = "house_party",
  FriendsParty = "friends_party",
  Afterwork = "afterwork",
  DancingLunch = "dancing_lunch",
  Diner = "diner",
  Garden = "garden",
  AfterBeach = "after_beach",
  Spectacle = "spectacle",
  Cruise = "cruise",
  OutsideAnimation = "outside_animation",
  Sport = "sport",
  Match = "match",
  Seminar = "seminar",
  Conference = "conference",
  WellnessDay = "wellness_day",
  Workshop = "workshop",
  TradeFair = "trade_fair",
  ConsumerShow = "consumer_show",
  Membership = "membership",
}

export enum OrganizationEventVisibilityType {
  Public = "public",
  Unlisted = "unlisted",
  Private = "private",
}

export enum OrganizationEventFileType {
  Flyer = "flyer",
  Trailer = "trailer",
}

export enum OrganizationEventStatus {
  Upcoming = "upcoming",
  Ongoing = "ongoing",
  Ended = "ended",
}

export type ExternalOffer = {
  name: string;
  description?: string;
  price: number;
  available: boolean;
};

export type ExternalContact = {
  type: "phone" | "email" | "website";
  value: string;
};

export type ExternalSource = {
  organizerName: string;
  contacts: ExternalContact[];
  offers: ExternalOffer[];
};

export type OrganizationEventRequestResponse = {
  contacts: ExternalContact[];
};

export type OrganizationEventNearbyOptions = ArrayOptions<OrganizationEvent> & {
  latitude: number;
  longitude: number;
  radius?: number;
};

export type OrganizationEventArrayOptions = ArrayOptions<OrganizationEvent> & {
  status?: OrganizationEventStatus | OrganizationEventStatus[];
  types?: OrganizationEventType | OrganizationEventType[];
  styles?: string | string[];
  city?: string;
  country?: string;
};

export type SearchOrganizationEventsOptions =
  ArrayOptions<OrganizationEvent> & {
    q: string;
  };

export type OrganizationEventCalendar = {
  [date: string]: OrganizationEvent[];
};

export type OrganizationEventEndpoints =
  | Endpoint<
      "GET",
      "/organizations/events/search",
      ArrayResult<OrganizationEvent>,
      SearchOrganizationEventsOptions
    >
  | Endpoint<
      "GET",
      "/organizations/events/calendar/:year/:month",
      OrganizationEventCalendar
    >
  | Endpoint<
      "GET",
      "/organizations/events",
      ArrayResult<OrganizationEvent>,
      OrganizationEventArrayOptions
    >
  | Endpoint<
      "GET",
      "/organizations/events/suggestions",
      ArrayResult<OrganizationEvent>,
      ArrayOptions<OrganizationEvent>
    >
  | Endpoint<
      "GET",
      "/organizations/events/nearby",
      ArrayResult<OrganizationEvent>,
      OrganizationEventNearbyOptions
    >
  | Endpoint<
      "GET",
      "/organizations/@:organizationSlug/events",
      ArrayResult<OrganizationEvent>,
      OrganizationEventArrayOptions
    >
  | Endpoint<
      "GET",
      "/organizations/@:organizationSlug/events/:eventSlug",
      OrganizationEvent
    >
  | Endpoint<
      "POST",
      "/organizations/@:organizationSlug/events",
      OrganizationEvent,
      CreateOrganizationEventDto
    >
  | Endpoint<
      "PUT",
      "/organizations/@:organizationSlug/events/:eventSlug",
      OrganizationEvent,
      UpdateOrganizationEventDto
    >
  | Endpoint<
      "DELETE",
      "/organizations/@:organizationSlug/events/:eventSlug",
      OrganizationEvent,
      null
    >
  | Endpoint<
      "POST",
      "/organizations/@:organizationSlug/events/:eventSlug/files/:eventFileType",
      string,
      FormData
    >
  | Endpoint<"POST", "/events/files/:eventFileType", string, FormData>
  | Endpoint<
      "POST",
      "/organizations/@:organizationSlug/events/:eventSlug/request",
      OrganizationEventRequestResponse
    >
  | OrganizationEventOrderEndpoints
  | OrganizationEventPromoCodeEndpoints
  | OrganizationEventStyleEndpoints
  | OrganizationEventTicketEndpoints
  | OrganizationEventViewEndpoints;
